import classNames from 'classnames';
import AsyncSelect from 'react-select/async';
import { __ } from '@wordpress/i18n';
import { useEffect, useState } from '@wordpress/element';

const selectStyle = {
    input: () => {
        return {
            padding: 0,
            margin: 0,
            borderColor: '#D0D5DD',
            height: '40px',
            alignItems: 'center',
            display: 'flex',
        };
    },
    placeholder: () => {
        return {
            fontSize: '14px',
            fontFamily: 'Inter',
            lineHeight: '24px',
            color: '#7D8292',
            borderColor: '#D0D5DD',
            marginLeft: '14px',
            position: 'absolute',
        };
    },
    control: (provided, state) => {
        const { isFocused } = state;
        return {
            ...provided,
            transition: 'background 0.3s ease',
            borderColor: isFocused ? '#5E23FF' : '#D0D5DD',
            boxShadow: isFocused ? '0px 0px 0px 4px #F4EBFF, 0px 1px 2px 0px rgba(16, 24, 40, 0.05)' : '0px 1px 1px 0px rgba(36, 36, 36, 0.05)',
            borderRadius: '8px',
            minHeight: '37px',
            '&:hover': {
                borderColor: isFocused ? '#5E23FF' : '#D0D5DD',
            }
        };
    },
    menuList: (provided) => {
        return {
            ...provided,
            paddingTop: '7px',
            paddingBottom: '7px'
        };
    },
    indicatorSeparator: () => {
        return {
            backgroundColor: 'transparent',
        };
    },
    indicatorsContainer: () => {
        return {
            fill: '#8181A5',
            display: 'flex'
        };
    },
    valueContainer: (provided, state) => {
        const { isMulti, hasValue } = state;
        return {
            ...provided,
            padding: isMulti && hasValue ? '2px' : '0 14px',
            minHeight: '37px',
        };
    },
    singleValue: (provided, state) => {
        const { hasValue } = state;
        return {
            color: hasValue ? '#28292C' : '#7D8292',
            fontSize: '12px',
            fontFamily: 'Inter',
            lineHeight: '18px',
            marginLeft: '14px',
            position: 'absolute',
            fontWeight: '500'
        };
    },
    option: (provided, state) => {
        return {
            ...provided,
            color: '#7D8292',
            fontSize: '14px',
            fontFamily: 'Inter',
            lineHeight: '24px',
            background: state.isFocused ? '#F6F4FF' : state.isSelected ? '#F6F4FF' : '#FFF',
            '&:hover': {
                background: '#F6F4FF',
                borderRadius: '3px'
            }
        };
    },
    dropdownIndicator: (provided, state) => {
        const { hasValue } = state;
        return {
            ...provided,
            color: hasValue ? '#0F1538' : '#7D8292',
        };
    },
    menu: (provided) => {
        return {
            ...provided,
            borderRadius: ' 14px',
            boxShadow: '0px 0px 30px 0px rgba(174, 170, 184, 0.25)',
            overflow: 'hidden',
            zIndex: '5'
        };
    },
    multiValue: (provided) => {
        return {
            ...provided,
            background: '#EAE6FF',
            color: '#5E23FF'
        };
    },
    multiValueLabel: (provided) => {
        return {
            ...provided,
            borderRadius: '3px',
            color: '#5E23FF',
            fontSize: '10px',
            fontFamily: 'Inter',
        };
    },
};

const SelectComponent = (props) => {

    const { innerProps, isSelected, isFocused } = props;

    const fontClass = classNames('font-option', {
        'selected': isSelected,
        'focused': isFocused,
    });

    return <>
        <div {...innerProps} className={fontClass} style={{
            fontFamily: 'Inter',
            padding: '10px 8px',
            margin: '0 7px',
            borderRadius: '8px',
            transition: 'background 0.3s ease, color 0.3s ease',
            color: isSelected ? 'rgba(94, 35, 255, 1)' : isFocused ? 'rgba(94, 35, 255, 1)' : '#7D8292',
            background: isSelected ? '#F6F4FF' : '#FFF',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 500,
            lineHeight: 'normal',
            alignItems: 'center',
            display: 'flex',
            cursor: 'pointer',
        }}>
            {props.data.label}
            {isSelected &&
                <div style={{ marginLeft: 'auto', alignItems: 'center', display: 'flex' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 16 16" fill="none">
                        <path d="M13.3346 4L6.0013 11.3333L2.66797 8" stroke="#5E23FF" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            }
        </div>
    </>;
};

const ControlSelectSearch = ({
    id,
    label,
    description,
    onSearch,
    value,
    updateValue,
    multiple,
    inputKey,
    width = '100%',
    isClearable = true
}) => {
    const [selectedValue, setSelectedValue] = useState([])
    const [loading, setLoading] = useState(true)
    const noOptionsMessage = () => __('Data not found', 'jeg-elementor-kit');
    useEffect(() => {

        onSearch({ include: value }).then(data => {
            setSelectedValue(data)
            setLoading(false)
        }).catch((error) => {
            setLoading(false)
        });
        if (value) {
            onSearch({ include: value }).then(data => {
                setSelectedValue(data)
                setLoading(false)
            }).catch((error) => {
                setLoading(false)
            });
        } else {
            setLoading(false)
        }
    }, [])

    const updateSelect = (options) => {
        setSelectedValue(options)
        if (options) {
            if (multiple) {
                const option = [];
                options.forEach(element => {
                    option.push(element.value);
                });
                updateValue(id, option.join(','));
            } else {
                updateValue(id, options.value.toString());
            }
        } else {
            updateValue(id, '');
        }
    };

    return <div className='jkit-control-wrapper'>
        <h3 className='control-label'>{label}</h3>
        <div id={`${inputKey}-select-control`} className={multiple ? 'jkit-select-control multiple' : 'jkit-select-control'}
            style={{
                width: width,
            }}
        >
            <AsyncSelect
                styles={selectStyle}
                defaultOptions={true}
                placeholder={__('Search...', 'jeg-elementor-kit')}
                noOptionsMessage={noOptionsMessage}
                isMulti={multiple > 1 ? true : false}
                value={selectedValue}
                onChange={option => {
                    updateSelect(option);
                }}
                isClearable={isClearable}
                isLoading={loading}
                loadOptions={input => onSearch({
                    type: 'search',
                    search: input,
                    exclude: multiple ? selectedValue.map((val) => parseInt(val.value)) : false,
                })}
                components={{
                    Option: SelectComponent,
                }} />
        </div>
        <p className='control-desc'>{description}</p>
    </div>;

    return;
};


export default ControlSelectSearch;